import {Injectable} from '@angular/core';
import {POIFilter} from './model/poi.filter';
import {Pageable} from '../../../../core/page/pageable';
import {Observable} from 'rxjs';
import {PageEntry} from '../../../../core/page/page.entry';
import {PageRestUtils} from '../../../../core/page/commons/page-rest.utils';
import {POIEntry} from './model/poi.entry';
import {IdValue} from '../../../../core/model/id-value';
import {POIForm} from './model/poi.form';
import {HttpClient, HttpParams} from '@angular/common/http';
import {HttpUtils} from '../../../../core/page/commons/http.utils';
import {PoiGroupDetails} from './model/poi-group-details';
import {map} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ExcelUploadStatus} from './model/excel-upload-status';
import {WorkTypeEntry} from '../../service/work-type/work-type.entry';
import {ServiceInfo} from '../../service/service-type/service-type.entry';

@Injectable()
export class POIService {

  constructor(private http: HttpClient,
              private translate: TranslateService) {
  }

  public getPOIs(filter: POIFilter, pageable: Pageable): Observable<PageEntry<POIEntry>> {
    let poiGroupId = filter.poiGroupId > 0 ? filter.poiGroupId.toString() : null;
    let params = PageRestUtils.appendPageableParams(pageable, new HttpParams());
    params = this.addFilterParams(params, poiGroupId, filter);

    return this.http.get<PageEntry<POIEntry>>('/api/poi', {params: params});
  }

  public getPOIsExcel(filter: POIFilter): Observable<Blob> {
    let poiGroupId = filter.poiGroupId > 0 ? filter.poiGroupId.toString() : null;
    let params = new HttpParams();
    params = this.addFilterParams(params, poiGroupId, filter);
    params = params.append('locale', this.translate.currentLang);

    return this.http.get('/api/poi/excel', {
      responseType: 'blob',
      params: params
    })
      .pipe(map(res => <Blob>res));
  }

  private addFilterParams(params, poiGroupId, filter: POIFilter) {
    params = HttpUtils.appendParam(params, 'poiGroupId', poiGroupId);
    params = HttpUtils.appendParam(params, 'name', filter.name);
    params = HttpUtils.appendParam(params, 'address', filter.address);
    params = HttpUtils.appendParam(params, 'country', filter.country);
    params = HttpUtils.appendParam(params, 'city', filter.city);
    params = HttpUtils.appendParam(params, 'postCode', filter.postCode);
    params = HttpUtils.appendParam(params, 'quantityFrom', filter.quantityFrom);
    params = HttpUtils.appendParam(params, 'quantityTo', filter.quantityTo);
    params = HttpUtils.appendParam(params, 'comment', filter.comment);
    params = HttpUtils.appendParam(params, 'poiGroupType', filter.poiGroupType);
    params = HttpUtils.appendParam(params, 'department', filter.department);
    return params;
  }

  public create(form: any): Observable<number> {
    return this.http.post<number>('/api/poi', form)
  }

  public editForm(id: number): Observable<POIForm> {
    return this.http.get<POIForm>('/api/poi/' + id);
  }

  public update(poiId: number, form: any): Observable<void> {
    return this.http.put<void>('/api/poi/' + poiId, form);
  }

  public delete(poiId: number): Observable<void> {
    return this.http.delete<void>('/api/poi/' + poiId, {});
  }

  public poiGroups(): Observable<IdValue<string>[]> {
    return this.http.get<IdValue<string>[]>('/api/poi-group/all');
  }

  public poiGroupsDetailed(): Observable<PoiGroupDetails[]> {
    return this.http.get<PoiGroupDetails[]>('/api/poi-group/all-detailed');
  }

  public workTypes(): Observable<WorkTypeEntry[]> {
    return this.http.get<WorkTypeEntry[]>('/api/work-types');
  }

  public periodicalServices(): Observable<ServiceInfo[]> {
    return this.http.get<ServiceInfo[]>('/api/service/all');
  }

  public getAddress(coordinates: string): Observable<string> {
    return this.http.get(`/api/poi/address?coordinates=` +
      encodeURIComponent(coordinates), {responseType: 'text'});
  }

  public uploadFile(formData: FormData): Observable<void> {
    return this.http.post<void>(`/api/poi/upload`, formData, {headers: {skip: 'true'}});
  }

  public uploadStatus(): Observable<ExcelUploadStatus> {
    return this.http.get<ExcelUploadStatus>(`/api/poi/upload/status`, {headers: {skip: 'true'}});
  }

  public uploadDKVFile(formData: FormData): Observable<void> {
    return this.http.post<void>(`/api/poi/upload-dkv`, formData, {headers: {skip: 'true'}});
  }

  public uploadDKVStatus(): Observable<ExcelUploadStatus> {
    return this.http.get<ExcelUploadStatus>(`/api/poi/upload-dkv/status`, {headers: {skip: 'true'}});
  }

  public downloadExample(type: string): Observable<Blob> {
    let params = new HttpParams();
    params = params.append('locale', this.translate.currentLang);
    params = params.append('exampleType', type);
    return this.http.get('/api/poi/download-excel-example', {responseType: 'blob', params: params})
      .pipe(map(response => <Blob>response))
  }

}
